import { Box, TextField, Typography, alpha, useTheme } from "@mui/material";
import { useMemo, useState, useEffect } from "react";

interface HistogramBucket {
  /** Label for this bucket (e.g., "0-10", "10-20") */
  label: string;
  /** Count of items in this bucket */
  count: number;
  /** Optional: min value of bucket range */
  min?: number;
  /** Optional: max value of bucket range */
  max?: number;
}

interface HistogramFilterProps {
  /** Histogram bucket data */
  buckets: HistogramBucket[];

  /** Title to show above histogram */
  title?: string;

  /** Whether to show count labels */
  showCounts?: boolean;

  /** Whether to show percentage labels */
  showPercentages?: boolean;

  /** Max height for the histogram */
  maxHeight?: number;

  /** Bar color */
  barColor?: string;

  /** Loading state */
  isLoading?: boolean;

  /** Orientation: horizontal bars or vertical bars */
  orientation?: "horizontal" | "vertical";

  /** Compact mode - single row sparkline with range inputs */
  compact?: boolean;

  /** Current range value for filtering */
  rangeValue?: { min?: number; max?: number };

  /** Callback when range changes */
  onRangeChange?: (range: { min?: number; max?: number } | undefined) => void;

  /** Min/max bounds from data */
  minBound?: number;
  maxBound?: number;
}

/**
 * Histogram visualization for filter value distributions
 *
 * Displays a read-only bar chart showing how values are distributed
 * across different ranges/buckets. Like Amazon's price range visualization.
 */
const HistogramFilter: React.FC<HistogramFilterProps> = ({
  buckets,
  title,
  showCounts = true,
  showPercentages = false,
  maxHeight = 200,
  barColor,
  isLoading = false,
  orientation = "horizontal",
  compact = true,
  rangeValue,
  onRangeChange,
  minBound,
  maxBound,
}) => {
  const theme = useTheme();
  const color = barColor || theme.palette.primary.main;

  // Local state for input fields
  const [minInput, setMinInput] = useState<string>("");
  const [maxInput, setMaxInput] = useState<string>("");

  // Sync local state with prop
  useEffect(() => {
    setMinInput(rangeValue?.min !== undefined ? String(rangeValue.min) : "");
    setMaxInput(rangeValue?.max !== undefined ? String(rangeValue.max) : "");
  }, [rangeValue]);

  // Calculate max count for scaling bars
  const maxCount = useMemo(() => {
    return Math.max(...buckets.map((b) => b.count), 1);
  }, [buckets]);

  // Calculate total for percentages
  const totalCount = useMemo(() => {
    return buckets.reduce((sum, b) => sum + b.count, 0);
  }, [buckets]);

  // Handle input blur - commit the change
  const handleMinBlur = () => {
    if (!onRangeChange) return;
    const min = minInput ? parseFloat(minInput) : undefined;
    const max = maxInput ? parseFloat(maxInput) : undefined;
    if (min === undefined && max === undefined) {
      onRangeChange(undefined);
    } else {
      onRangeChange({ min, max });
    }
  };

  const handleMaxBlur = () => {
    if (!onRangeChange) return;
    const min = minInput ? parseFloat(minInput) : undefined;
    const max = maxInput ? parseFloat(maxInput) : undefined;
    if (min === undefined && max === undefined) {
      onRangeChange(undefined);
    } else {
      onRangeChange({ min, max });
    }
  };

  // Handle enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 0.5 }}>
        <Box
          sx={{
            height: 24,
            backgroundColor: alpha(theme.palette.action.hover, 0.3),
            borderRadius: 1,
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.5 },
            },
          }}
        />
      </Box>
    );
  }

  if (buckets.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 1, fontStyle: "italic", fontSize: "0.75rem" }}
      >
        No data available
      </Typography>
    );
  }

  // Compact mode - sparkline with range inputs
  if (compact) {
    return (
      <Box>
        {/* Sparkline histogram - single row of vertical bars */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: "1px",
            height: 32,
            mb: 1,
            px: 0.5,
          }}
        >
          {buckets.map((bucket, index) => {
            const heightPercent = (bucket.count / maxCount) * 100;
            return (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: `${Math.max(
                    heightPercent,
                    bucket.count > 0 ? 8 : 2,
                  )}%`,
                  minHeight: 2,
                  backgroundColor: alpha(color, 0.5),
                  borderRadius: "2px 2px 0 0",
                  transition: "height 0.2s ease",
                  "&:hover": {
                    backgroundColor: alpha(color, 0.8),
                  },
                }}
                title={`${bucket.label}: ${bucket.count}`}
              />
            );
          })}
        </Box>

        {/* Range labels */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            px: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", color: theme.palette.text.disabled }}
          >
            {minBound !== undefined
              ? minBound.toFixed(2)
              : buckets[0]?.label.split("-")[0] || "0"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", color: theme.palette.text.disabled }}
          >
            {totalCount.toLocaleString()} total
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", color: theme.palette.text.disabled }}
          >
            {maxBound !== undefined
              ? maxBound.toFixed(2)
              : buckets[buckets.length - 1]?.label.split("-")[1] || "1"}
          </Typography>
        </Box>

        {/* Range inputs */}
        {onRangeChange && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Min"
              value={minInput}
              onChange={(e) => setMinInput(e.target.value)}
              onBlur={handleMinBlur}
              onKeyDown={handleKeyDown}
              type="number"
              inputProps={{
                step: "any",
                style: { textAlign: "center", padding: "4px 8px" },
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled }}
            >
              to
            </Typography>
            <TextField
              size="small"
              placeholder="Max"
              value={maxInput}
              onChange={(e) => setMaxInput(e.target.value)}
              onBlur={handleMaxBlur}
              onKeyDown={handleKeyDown}
              type="number"
              inputProps={{
                step: "any",
                style: { textAlign: "center", padding: "4px 8px" },
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  if (orientation === "vertical") {
    // Vertical bar chart (like a traditional histogram)
    return (
      <Box>
        {title && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            {title}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 0.5,
            height: maxHeight,
            pt: 2,
          }}
        >
          {buckets.map((bucket, index) => {
            const heightPercent = (bucket.count / maxCount) * 100;
            const percentage =
              totalCount > 0 ? (bucket.count / totalCount) * 100 : 0;

            return (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 0,
                }}
              >
                {/* Count/percentage label */}
                {(showCounts || showPercentages) && bucket.count > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: theme.palette.text.secondary,
                      mb: 0.25,
                    }}
                  >
                    {showPercentages
                      ? `${percentage.toFixed(0)}%`
                      : bucket.count.toLocaleString()}
                  </Typography>
                )}
                {/* Bar */}
                <Box
                  sx={{
                    width: "100%",
                    height: `${Math.max(heightPercent, 2)}%`,
                    backgroundColor: alpha(color, 0.7),
                    borderRadius: "2px 2px 0 0",
                    transition: "height 0.3s ease",
                    minHeight: bucket.count > 0 ? 4 : 1,
                    "&:hover": {
                      backgroundColor: color,
                    },
                  }}
                />
                {/* Bucket label */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    color: theme.palette.text.disabled,
                    mt: 0.5,
                    writingMode:
                      buckets.length > 8 ? "vertical-rl" : "horizontal-tb",
                    textOrientation: "mixed",
                    transform: buckets.length > 8 ? "rotate(180deg)" : "none",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {bucket.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Horizontal bar chart (default - like Amazon's price ranges)
  return (
    <Box>
      {title && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          maxHeight: maxHeight,
          overflowY: buckets.length > 10 ? "auto" : "visible",
          pr: buckets.length > 10 ? 0.5 : 0,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: "2px",
          },
        }}
      >
        {buckets.map((bucket, index) => {
          const widthPercent = (bucket.count / maxCount) * 100;
          const percentage =
            totalCount > 0 ? (bucket.count / totalCount) * 100 : 0;

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Bucket label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.text.secondary,
                  minWidth: 50,
                  flexShrink: 0,
                }}
              >
                {bucket.label}
              </Typography>

              {/* Bar container */}
              <Box
                sx={{
                  flex: 1,
                  height: 16,
                  backgroundColor: alpha(theme.palette.action.hover, 0.3),
                  borderRadius: 1,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* Bar fill */}
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.max(
                      widthPercent,
                      bucket.count > 0 ? 2 : 0,
                    )}%`,
                    backgroundColor: alpha(color, 0.6),
                    borderRadius: 1,
                    transition: "width 0.3s ease",
                    "&:hover": {
                      backgroundColor: alpha(color, 0.8),
                    },
                  }}
                />
              </Box>

              {/* Count/percentage */}
              {(showCounts || showPercentages) && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    color: theme.palette.text.disabled,
                    minWidth: 35,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {showPercentages
                    ? `${percentage.toFixed(0)}%`
                    : bucket.count.toLocaleString()}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Summary */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1,
          color: theme.palette.text.disabled,
          fontSize: "0.7rem",
          textAlign: "right",
        }}
      >
        Total: {totalCount.toLocaleString()}
      </Typography>
    </Box>
  );
};

export default HistogramFilter;
